import { useState, useEffect } from "react";
import type { AxiosInstance } from "axios";
import { readCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";
import { fetchFwaasReading, type FwaasReadingKey, type FwaasReadings } from "./fwaasReading";

/** `undefined` while on its way; `null` when the firewall could not answer. */
export type Reading<T> = T | null | undefined;

/**
 * Load one technical reading of the detail (policies, routing, NAT, events). Each is its
 * own request, in parallel with the overview, so a slow one never holds the others.
 */
export function useFwaasReading<K extends FwaasReadingKey>(
    key: K,
    fwaasId: string | null,
    api: AxiosInstance,
    refresh: number,
    tenantId: number | null
): Reading<FwaasReadings[K]> {
    const cacheKey =
        fwaasId && tenantId != null ? `kontroledge/fwaas/${fwaasId}/${key}?tenantId=${tenantId}` : null;
    const [reading, setReading] = useState<Reading<FwaasReadings[K]>>(() =>
        readCachedFetch<FwaasReadings[K] | null>(cacheKey)
    );

    useEffect(() => {
        if (!fwaasId || tenantId == null) return;

        let cancelled = false;
        // Another firewall on the same mounted page: its own cached reading or the
        // skeleton — never the previous firewall's.
        const cached = readCachedFetch<FwaasReadings[K] | null>(cacheKey);
        setReading(cached);

        fetchFwaasReading(api, tenantId, fwaasId, key)
            .then((loaded) => {
                if (cancelled) return;
                writeCachedFetch(cacheKey, loaded);
                setReading(loaded);
            })
            .catch(() => {
                if (cancelled) return;
                if (cached === undefined) setReading(null);
            });

        return () => {
            cancelled = true;
        };
    }, [key, fwaasId, tenantId, api, refresh, cacheKey]);

    return reading;
}
